import * as React from "react";
import { useQuery } from "@tanstack/react-query";

import { API_URL, downloadBlob, downloadText, exportAccountingTouchDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDashboardRoles } from "@/pages/dashboard/useDashboardRoles";


export function currentCalendarMonthRange(): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const pad = (n: number) => String(n).padStart(2, "0");
  const last = new Date(y, m, 0).getDate();
  return { from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${pad(last)}` };
}

export function accountingTouchDetailFilename(from: string, to: string, format: "csv" | "xlsx"): string {
  const fromParts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(from);
  const toParts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(to);
  if (fromParts && toParts) {
    const y = Number(fromParts[1]);
    const mo = Number(fromParts[2]);
    const d0 = Number(fromParts[3]);
    const y2 = Number(toParts[1]);
    const mo2 = Number(toParts[2]);
    const d1 = Number(toParts[3]);
    const last = new Date(y, mo, 0).getDate();
    if (d0 === 1 && y === y2 && mo === mo2 && d1 === last) {
      return `AccountingTouchDetail_${y}-${String(mo).padStart(2, "0")}.${format}`;
    }
  }
  return `AccountingTouchDetail_${from}_to_${to}.${format}`;
}


export function useAccountingTouchDetailViewModel() {
  const { user } = useAuth();
  const { isAdmin } = useDashboardRoles();
  const [accountingFromDate, setAccountingFromDate] = React.useState(() => currentCalendarMonthRange().from);
  const [accountingToDate, setAccountingToDate] = React.useState(() => currentCalendarMonthRange().to);
  const [accountingCoCode, setAccountingCoCode] = React.useState<string[]>(["LXB"]);
  const [accountingOfficeLocation, setAccountingOfficeLocation] = React.useState<string[]>([]);
  const [accountingFormat, setAccountingFormat] = React.useState<"csv" | "xlsx">("csv");
  const [accountingExportError, setAccountingExportError] = React.useState<string | null>(null);

  const { data: payrollCoCodesData, isLoading: payrollCoCodesLoading, error: payrollCoCodesError } = useQuery({
    queryKey: ["payroll-co-codes"],
    queryFn: async () => {
      const resp = await fetch(`${API_URL}/entries/co-codes`, { credentials: "include" });
      if (!resp.ok) return { co_codes: [] as string[], companies: {} as Record<string, string> };
      return resp.json() as Promise<{ co_codes: string[]; companies?: Record<string, string> }>;
    },
    enabled: !!isAdmin,
  });
  const { data: officeLocationsData, isLoading: officeLocationsLoading, error: officeLocationsError } = useQuery({
    queryKey: ["office-locations"],
    queryFn: async () => {
      const resp = await fetch(`${API_URL}/entries/office-locations`, { credentials: "include" });
      if (!resp.ok) return { office_locations: [] as string[] };
      return resp.json() as Promise<{ office_locations: string[] }>;
    },
    enabled: !!isAdmin,
  });

  return {
    isAdmin,
    accountingFromDate, setAccountingFromDate,
    accountingToDate, setAccountingToDate,
    accountingCoCode, setAccountingCoCode,
    accountingOfficeLocation, setAccountingOfficeLocation,
    accountingFormat, setAccountingFormat,
    accountingExportError, setAccountingExportError,
    payrollCoCodesData, payrollCoCodesLoading, payrollCoCodesError,
    officeLocationsData, officeLocationsLoading, officeLocationsError,
    currentCalendarMonthRange,
    accountingTouchDetailFilename,
    downloadBlob, downloadText, exportAccountingTouchDetail,
  };
}
